import React, { useState, useEffect } from 'react';
import { 
  Undo2, 
  Plus, 
  MoreVertical,
  Trash2,
  CheckCircle,
  Clock,
  PackageX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-blue-50 text-blue-700 border-blue-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200'
};

const PurchaseReturns = () => {
  const { currencySymbol } = useSettings();
  const [returns, setReturns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({ items: [] });
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchReturns();
    fetchSuppliers();
    fetchProducts();
  }, []);

  const fetchReturns = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/returns`);
      setReturns(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/suppliers`);
      setSuppliers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/procurement/products`);
      setProducts(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/procurement/returns`, formData);
      toast.success('Return created');
      setShowDialog(false);
      setFormData({ items: [] });
      fetchReturns();
    } catch (err) {
      toast.error('Failed to create return');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/procurement/returns/${id}/status?status=${status}`);
      toast.success('Status updated');
      fetchReturns();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this return?')) {
      try {
        await axios.delete(`${API_URL}/api/procurement/returns/${id}`);
        toast.success('Return deleted');
        fetchReturns();
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const openNew = () => {
    setFormData({ items: [{ product_id: '', quantity: 1, unit_price: 0, reason: '' }] });
    setShowDialog(true);
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { product_id: '', quantity: 1, unit_price: 0, reason: '' }]
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) newItems[index].unit_price = product.cost_price;
    }
    setFormData({ ...formData, items: newItems });
  };

  const removeItem = (index) => {
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const calculateTotal = () => formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);

  const filteredReturns = returns.filter(r => filterStatus === 'all' || r.status === filterStatus);

  const stats = {
    total: returns.length,
    pending: returns.filter(r => r.status === 'pending').length,
    totalValue: returns.reduce((sum, r) => sum + (r.total || 0), 0)
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchase Returns</h1>
          <p className="text-muted-foreground">Return goods to suppliers.</p>
        </div>
        <Button onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> New Return</Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">Total Returns</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-amber-600">{stats.pending}</div><div className="text-sm text-muted-foreground">Pending</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-red-600">{currencySymbol} {stats.totalValue.toLocaleString()}</div><div className="text-sm text-muted-foreground">Total Value</div></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Return #</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Items</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredReturns.map((ret) => (
              <TableRow key={ret.id}>
                <TableCell className="font-mono font-medium">{ret.return_number}</TableCell>
                <TableCell>{ret.supplier_name}</TableCell>
                <TableCell className="text-muted-foreground">{format(new Date(ret.date), 'MMM d, yyyy')}</TableCell>
                <TableCell>{ret.items?.length || 0} items</TableCell>
                <TableCell className="text-right font-mono font-bold text-red-600">{currencySymbol} {ret.total?.toLocaleString()}</TableCell>
                <TableCell className="max-w-xs truncate text-muted-foreground">{ret.reason || '-'}</TableCell>
                <TableCell><Badge className={statusColors[ret.status]}>{ret.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {ret.status === 'pending' && (
                        <DropdownMenuItem onClick={() => handleUpdateStatus(ret.id, 'approved')}><CheckCircle className="h-4 w-4 mr-2" /> Approve</DropdownMenuItem>
                      )}
                      {ret.status === 'approved' && (
                        <DropdownMenuItem onClick={() => handleUpdateStatus(ret.id, 'completed')}><CheckCircle className="h-4 w-4 mr-2" /> Complete</DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(ret.id)}>
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredReturns.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-32 text-center">
                  <Undo2 className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No returns found</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Return Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New Purchase Return</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Supplier *</Label>
              <Select value={formData.supplier_id || ''} onValueChange={v => setFormData({...formData, supplier_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason for Return</Label>
              <Input value={formData.reason || ''} onChange={e => setFormData({...formData, reason: e.target.value})} placeholder="Defective, Wrong items, etc." />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Items</Label>
                <Button type="button" variant="outline" size="sm" onClick={addItem}><Plus className="h-3 w-3 mr-1" /> Add Item</Button>
              </div>
              <div className="space-y-2 border rounded-lg p-3">
                {formData.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Select className="col-span-4" value={item.product_id} onValueChange={v => updateItem(idx, 'product_id', v)}>
                      <SelectTrigger className="col-span-4"><SelectValue placeholder="Product" /></SelectTrigger>
                      <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="col-span-2" type="number" min="1" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} />
                    <Input className="col-span-2" type="number" step="0.01" placeholder="Price" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                    <Input className="col-span-3" placeholder="Reason" value={item.reason || ''} onChange={e => updateItem(idx, 'reason', e.target.value)} />
                    <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(idx)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex justify-between font-bold text-lg"><span>Total</span><span className="font-mono text-red-600">{currencySymbol} {calculateTotal().toFixed(2)}</span></div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={formData.notes || ''} onChange={e => setFormData({...formData, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}><PackageX className="h-4 w-4 mr-2" /> Create Return</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseReturns;
